import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { 
  Building2, 
  Users, 
  FileText, 
  DollarSign,
  Calendar,
  Loader2,
  User,
  Eye,
  FileSpreadsheet,
  FileText as FileTextIcon
} from 'lucide-react';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/formatters';
import { formatDate, parseCivilDateString } from '@/lib/utils';
import ClienteFornecedorModal from '@/components/ClientesFornecedores/ClienteFornecedorModal';

interface InadimplenciaClientsModalProps {
  isOpen: boolean;
  onClose: () => void;
  category: '1-2' | '3-6' | '7+' | null;
  selectedCompany?: string;
}

interface ClienteData {
  id: string;
  nome: string;
  documento: string;
  email: string;
  titulos: any[];
  valorTotal: number;
  quantidadeTitulos: number;
  titulosVencidos: number;
  titulosAbertos: number;
  titulosParciais: number;
  titulosFechados: number;
  valorVencido: number;
  valorAberto: number;
  valorParcial: number;
  valorFechado: number;
  empresa?: string;
  operador?: string;
  companyId?: string;
  company?: {
    id: string;
    name: string;
  };
  operadorObj?: {
    id: string;
    name: string;
    email: string;
  };
  kanbanEtapa?: {
    id: string;
    nome: string;
    cor: string;
  };
}

export const InadimplenciaClientsModal: React.FC<InadimplenciaClientsModalProps> = ({
  isOpen,
  onClose,
  category,
  selectedCompany
}) => {
  const [selectedCliente, setSelectedCliente] = useState<ClienteData | null>(null);
  const [isClienteModalOpen, setIsClienteModalOpen] = useState(false);

  // Função para abrir timeline do cliente em nova aba
  const handleOpenClienteTimeline = async (cliente: ClienteData) => {
    try {
      // Buscar dados completos do cliente
      const response = await api.get(`/clientes-fornecedores/${cliente.id}`);
      const clienteCompleto = response.data.data;
      
      // Criar uma página HTML simples com a timeline
      const htmlContent = `
        <!DOCTYPE html>
        <html lang="pt-BR">
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Timeline - ${cliente.nome}</title>
            <style>
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              body {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                background: #f5f5f5;
                padding: 20px;
                color: #333;
              }
              .container {
                max-width: 1200px;
                margin: 0 auto;
                background: white;
                border-radius: 8px;
                box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                padding: 30px;
              }
              .header {
                border-bottom: 2px solid #3b82f6;
                padding-bottom: 20px;
                margin-bottom: 30px;
              }
              .header h1 {
                color: #1f2937;
                font-size: 24px;
                margin-bottom: 10px;
              }
              .header-info {
                display: flex;
                gap: 20px;
                flex-wrap: wrap;
                color: #6b7280;
                font-size: 14px;
              }
              .header-info span {
                display: flex;
                align-items: center;
                gap: 5px;
              }
              .timeline {
                position: relative;
                padding-left: 40px;
              }
              .timeline::before {
                content: '';
                position: absolute;
                left: 15px;
                top: 0;
                bottom: 0;
                width: 2px;
                background: #e5e7eb;
              }
              .timeline-item {
                position: relative;
                margin-bottom: 30px;
                padding: 20px;
                background: #f9fafb;
                border-radius: 8px;
                border-left: 4px solid #3b82f6;
              }
              .timeline-item::before {
                content: '';
                position: absolute;
                left: -32px;
                top: 25px;
                width: 12px;
                height: 12px;
                border-radius: 50%;
                background: #3b82f6;
                border: 3px solid white;
                box-shadow: 0 0 0 2px #e5e7eb;
              }
              .timeline-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 10px;
              }
              .timeline-type {
                font-weight: 600;
                color: #3b82f6;
                font-size: 14px;
              }
              .timeline-date {
                color: #6b7280;
                font-size: 12px;
              }
              .timeline-content {
                color: #374151;
                line-height: 1.6;
                margin-bottom: 10px;
              }
              .timeline-user {
                color: #6b7280;
                font-size: 12px;
                display: flex;
                align-items: center;
                gap: 5px;
              }
              .empty-state {
                text-align: center;
                padding: 60px 20px;
                color: #9ca3af;
              }
              .empty-state svg {
                width: 64px;
                height: 64px;
                margin: 0 auto 20px;
                opacity: 0.5;
              }
              .timeline-section {
                margin-top: 15px;
                padding-top: 15px;
                border-top: 1px solid #e5e7eb;
              }
              .timeline-section-title {
                font-weight: 600;
                color: #374151;
                font-size: 13px;
                margin-bottom: 10px;
              }
              .titulos-list {
                display: flex;
                flex-direction: column;
                gap: 8px;
              }
              .titulo-item {
                padding: 10px;
                background: white;
                border-radius: 6px;
                border: 1px solid #e5e7eb;
              }
              .titulo-info {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 5px;
              }
              .titulo-numero {
                font-weight: 600;
                color: #1f2937;
                font-size: 13px;
              }
              .titulo-valor {
                font-weight: 600;
                color: #059669;
                font-size: 13px;
              }
              .titulo-detalhes {
                display: flex;
                gap: 15px;
                flex-wrap: wrap;
                font-size: 11px;
                color: #6b7280;
              }
              .comprovantes-list {
                display: flex;
                flex-direction: column;
                gap: 8px;
              }
              .comprovante-item {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 10px;
                background: white;
                border-radius: 6px;
                border: 1px solid #e5e7eb;
              }
              .comprovante-info {
                display: flex;
                align-items: center;
                gap: 8px;
                font-size: 13px;
                color: #374151;
              }
              .comprovante-link {
                padding: 5px 12px;
                background: #3b82f6;
                color: white;
                text-decoration: none;
                border-radius: 4px;
                font-size: 12px;
                font-weight: 500;
                transition: background 0.2s;
              }
              .comprovante-link:hover {
                background: #2563eb;
              }
              .pagamentos-list {
                display: flex;
                flex-direction: column;
                gap: 8px;
              }
              .pagamento-item {
                padding: 10px;
                background: white;
                border-radius: 6px;
                border: 1px solid #e5e7eb;
              }
              .pagamento-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 8px;
              }
              .pagamento-valor {
                font-weight: 600;
                color: #059669;
                font-size: 14px;
              }
              .pagamento-status {
                padding: 3px 10px;
                border-radius: 12px;
                font-size: 11px;
                font-weight: 600;
              }
              .status-confirmado {
                background: #d1fae5;
                color: #059669;
              }
              .status-pendente {
                background: #fef3c7;
                color: #d97706;
              }
              .status-cancelado {
                background: #fee2e2;
                color: #dc2626;
              }
            </style>
          </head>
          <body>
            <div class="container">
              <div class="header">
                <h1>Timeline - ${cliente.nome || 'Cliente'}</h1>
                <div class="header-info">
                  ${cliente.documento ? `<span>📄 Doc: ${cliente.documento}</span>` : ''}
                  ${cliente.email ? `<span>✉️ ${cliente.email}</span>` : ''}
                  ${cliente.quantidadeTitulos ? `<span>📋 ${cliente.quantidadeTitulos} título(s)</span>` : ''}
                  ${cliente.valorTotal ? `<span>💰 ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cliente.valorTotal)}</span>` : ''}
                  ${clienteCompleto.titulos && clienteCompleto.titulos.length > 0 
                    ? `<span>📊 Total de Títulos: ${clienteCompleto.titulos.length}</span>` 
                    : ''}
                </div>
              </div>
              
              ${clienteCompleto.titulos && clienteCompleto.titulos.length > 0 
                ? `
                <div style="margin-bottom: 30px; padding: 20px; background: #f9fafb; border-radius: 8px; border-left: 4px solid #3b82f6;">
                  <h2 style="font-size: 18px; color: #1f2937; margin-bottom: 15px;">📋 Títulos do Cliente</h2>
                  <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 15px;">
                    ${clienteCompleto.titulos
                      .sort((a: any, b: any) => {
                        // Ordenar do mais recente para o mais antigo
                        // Priorizar por createdAt, depois por vencimento
                        const dateA = a.createdAt
                          ? new Date(a.createdAt).getTime()
                          : a.vencimento
                            ? parseCivilDateString(a.vencimento).getTime()
                            : 0;
                        const dateB = b.createdAt
                          ? new Date(b.createdAt).getTime()
                          : b.vencimento
                            ? parseCivilDateString(b.vencimento).getTime()
                            : 0;
                        return dateB - dateA; // Mais recente primeiro
                      })
                      .map((titulo: any) => {
                      const vencimento = titulo.vencimento ? formatDate(titulo.vencimento) : 'N/A';
                      const statusColor = titulo.status === 'VENCIDO' ? '#dc2626' 
                        : titulo.status === 'PARCIAL' ? '#f59e0b'
                        : titulo.status === 'FECHADO' ? '#059669'
                        : '#3b82f6';
                      return `
                        <div style="padding: 15px; background: white; border-radius: 6px; border-left: 3px solid ${statusColor}; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
                          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                            <span style="font-weight: 600; color: #1f2937;">${titulo.numero || 'N/A'}</span>
                            <span style="padding: 3px 8px; background: ${statusColor}15; color: ${statusColor}; border-radius: 12px; font-size: 11px; font-weight: 600;">${titulo.status || 'N/A'}</span>
                          </div>
                          <div style="font-size: 14px; font-weight: 600; color: #059669; margin-bottom: 5px;">
                            ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(titulo.valor || 0)}
                          </div>
                          <div style="font-size: 12px; color: #6b7280; display: flex; gap: 15px; flex-wrap: wrap;">
                            <span>Vencimento: ${vencimento}</span>
                            ${titulo.saldo ? `<span>Saldo: ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(titulo.saldo)}</span>` : ''}
                          </div>
                        </div>
                      `;
                    }).join('')}
                  </div>
                </div>
                `
                : ''
              }
              <div class="timeline">
                ${clienteCompleto.feedbacks && clienteCompleto.feedbacks.length > 0 
                  ? clienteCompleto.feedbacks
                      .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                      .map((feedback: any) => {
                      const tipoLabel = feedback.tipo === 'LIGACAO' ? '📞 Ligação' 
                        : feedback.tipo === 'EMAIL' ? '✉️ Email'
                        : feedback.tipo === 'WHATSAPP' ? '💬 WhatsApp'
                        : feedback.tipo === 'VISITA' ? '📍 Visita'
                        : '💬 Contato';
                      const data = new Date(feedback.createdAt);
                      const dataFormatada = data.toLocaleDateString('pt-BR', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      });
                      // Mesma origem no dev (Vite faz proxy de /api para a API)
                      const apiBaseUrl = window.location.origin;
                      
                      const getFileUrl = (fileUrl: string) => {
                        if (!fileUrl) return '';
                        if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
                          return fileUrl;
                        }
                        if (fileUrl.startsWith('/api/v1')) {
                          return `${apiBaseUrl}${fileUrl}`;
                        }
                        if (fileUrl.startsWith('/uploads')) {
                          return `${window.location.origin}${fileUrl}`;
                        }
                        return `${apiBaseUrl}/api/v1${fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`}`;
                      };
                      
                      // Títulos negociados
                      const titulosHtml = feedback.titulosNegociadosData && feedback.titulosNegociadosData.length > 0
                        ? `
                          <div class="timeline-section">
                            <div class="timeline-section-title">📋 Títulos Relacionados (${feedback.titulosNegociadosData.length})</div>
                            <div class="titulos-list">
                              ${feedback.titulosNegociadosData
                                .sort((a: any, b: any) => {
                                  // Ordenar do mais recente para o mais antigo
                                  const dateA = a.createdAt
                                    ? new Date(a.createdAt).getTime()
                                    : a.vencimento
                                      ? parseCivilDateString(a.vencimento).getTime()
                                      : 0;
                                  const dateB = b.createdAt
                                    ? new Date(b.createdAt).getTime()
                                    : b.vencimento
                                      ? parseCivilDateString(b.vencimento).getTime()
                                      : 0;
                                  return dateB - dateA; // Mais recente primeiro
                                })
                                .map((titulo: any) => {
                                const vencimento = titulo.vencimento ? formatDate(titulo.vencimento) : 'N/A';
                                return `
                                  <div class="titulo-item">
                                    <div class="titulo-info">
                                      <span class="titulo-numero">${titulo.numero || 'N/A'}</span>
                                      <span class="titulo-valor">${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(titulo.valor || 0)}</span>
                                    </div>
                                    <div class="titulo-detalhes">
                                      <span>Status: ${titulo.status || 'N/A'}</span>
                                      <span>Vencimento: ${vencimento}</span>
                                      ${titulo.company?.name ? `<span>Empresa: ${titulo.company.name}</span>` : ''}
                                    </div>
                                  </div>
                                `;
                              }).join('')}
                            </div>
                          </div>
                        `
                        : '';
                      
                      // Comprovantes/Anexos
                      const comprovantesHtml = feedback.comprovantes && feedback.comprovantes.length > 0
                        ? `
                          <div class="timeline-section">
                            <div class="timeline-section-title">📎 Anexos/Comprovantes (${feedback.comprovantes.length})</div>
                            <div class="comprovantes-list">
                              ${feedback.comprovantes.map((comprovante: any) => {
                                const fileUrl = getFileUrl(comprovante.url);
                                const fileSize = comprovante.fileSize ? (comprovante.fileSize / 1024).toFixed(2) + ' KB' : '';
                                return `
                                  <div class="comprovante-item">
                                    <div class="comprovante-info">
                                      <span>📄 ${comprovante.fileName || comprovante.originalName || 'Arquivo'}</span>
                                      ${fileSize ? `<span style="color: #6b7280; font-size: 11px;">(${fileSize})</span>` : ''}
                                    </div>
                                    ${fileUrl ? `<a href="${fileUrl}" target="_blank" class="comprovante-link">Abrir</a>` : ''}
                                  </div>
                                `;
                              }).join('')}
                            </div>
                          </div>
                        `
                        : '';
                      
                      // Histórico de Pagamentos
                      const pagamentosHtml = feedback.historicoPagamentos && feedback.historicoPagamentos.length > 0
                        ? `
                          <div class="timeline-section">
                            <div class="timeline-section-title">💰 Pagamentos Registrados (${feedback.historicoPagamentos.length})</div>
                            <div class="pagamentos-list">
                              ${feedback.historicoPagamentos.map((pagamento: any) => {
                                const dataPagamento = pagamento.dataPagamento ? new Date(pagamento.dataPagamento).toLocaleDateString('pt-BR') : 'N/A';
                                const statusClass = pagamento.statusPagamento === 'CONFIRMADO' ? 'status-confirmado'
                                  : pagamento.statusPagamento === 'PENDENTE' ? 'status-pendente'
                                  : 'status-cancelado';
                                const statusLabel = pagamento.statusPagamento === 'CONFIRMADO' ? 'Confirmado'
                                  : pagamento.statusPagamento === 'PENDENTE' ? 'Pendente'
                                  : pagamento.statusPagamento === 'CANCELADO' ? 'Cancelado'
                                  : pagamento.statusPagamento || 'N/A';
                                return `
                                  <div class="pagamento-item">
                                    <div class="pagamento-header">
                                      <span class="pagamento-valor">${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(pagamento.valorNegociado || pagamento.valorPago || 0)}</span>
                                      <span class="pagamento-status ${statusClass}">${statusLabel}</span>
                                    </div>
                                    <div style="font-size: 12px; color: #6b7280;">
                                      <div>Título: ${pagamento.titulo?.numero || 'N/A'}</div>
                                      <div>Data: ${dataPagamento}</div>
                                      ${pagamento.formaPagamento ? `<div>Forma: ${pagamento.formaPagamento}</div>` : ''}
                                      ${pagamento.observacoes ? `<div style="margin-top: 5px; font-style: italic;">${pagamento.observacoes}</div>` : ''}
                                    </div>
                                  </div>
                                `;
                              }).join('')}
                            </div>
                          </div>
                        `
                        : '';
                      
                      return `
                        <div class="timeline-item">
                          <div class="timeline-header">
                            <span class="timeline-type">${tipoLabel}</span>
                            <span class="timeline-date">${dataFormatada}</span>
                          </div>
                          <div class="timeline-content">${feedback.descricao || 'Sem descrição'}</div>
                          ${feedback.user ? `<div class="timeline-user">👤 ${feedback.user.name || 'Usuário'}</div>` : ''}
                          ${titulosHtml}
                          ${comprovantesHtml}
                          ${pagamentosHtml}
                        </div>
                      `;
                    }).join('')
                  : `
                    <div class="empty-state">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                      </svg>
                      <p>Nenhum contato registrado</p>
                      <p style="font-size: 12px; margin-top: 5px;">Os contatos aparecerão aqui quando forem registrados</p>
                    </div>
                  `
                }
              </div>
            </div>
          </body>
        </html>
      `;
      
      // Abrir nova aba com o conteúdo HTML
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(htmlContent);
        newWindow.document.close();
      }
    } catch (error) {
      console.error('Erro ao abrir timeline:', error);
      // Fallback: abrir página de clientes
      window.open(`/clientes-fornecedores?clienteId=${cliente.id}`, '_blank');
    }
  };

  const handleClienteClick = (cliente: ClienteData) => {
    // Debug: verificar dados recebidos
    console.log('🔍 Dados do cliente recebidos:', {
      id: cliente.id,
      nome: cliente.nome,
      empresa: cliente.empresa,
      companyId: cliente.companyId,
      company: cliente.company,
      titulos: cliente.titulos?.length || 0
    });
    
    // Transformar dados da inadimplência para o formato esperado pelo ClienteFornecedorModal
    const clienteFormatted = {
      id: cliente.id,
      codigo: cliente.documento || '',
      nome: cliente.nome,
      descricao: '',
      whatsapp: '',
      email: cliente.email || '',
      companyId: cliente.titulos[0]?.companyId || '',
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      company: {
        id: cliente.companyId || cliente.titulos[0]?.companyId || '',
        name: cliente.company?.name || cliente.empresa || cliente.titulos[0]?.company?.name || cliente.titulos[0]?.empresa || 'Empresa não identificada'
      },
      titulosEmAberto: cliente.titulosAbertos || 0,
      valorTotalEmAberto: cliente.valorAberto || 0,
      titulosVencidos: cliente.titulosVencidos || 0,
      valorTotalVencido: cliente.valorVencido || 0,
      totalTitulos: cliente.quantidadeTitulos || 0,
      operadorAtual: cliente.operadorObj || (cliente.operador ? {
        id: '',
        name: typeof cliente.operador === 'string' ? cliente.operador : (cliente.operador as any)?.name || '',
        email: typeof cliente.operador === 'string' ? '' : (cliente.operador as any)?.email || ''
      } : undefined),
      titulos: cliente.titulos || [],
      feedbacks: [],
      historicoContatos: [],
      _count: {
        feedbacks: 0,
        titulos: cliente.quantidadeTitulos || 0
        }
      };
      
      // Debug: verificar dados formatados
      console.log('🔍 Dados formatados para o modal:', {
        id: clienteFormatted.id,
        nome: clienteFormatted.nome,
        companyId: clienteFormatted.companyId,
        companyName: clienteFormatted.company.name,
        titulos: clienteFormatted.titulos?.length || 0
      });
      
      setSelectedCliente(clienteFormatted as any);
      setIsClienteModalOpen(true);
  };

  const handleCloseClienteModal = () => {
    setIsClienteModalOpen(false);
    setSelectedCliente(null);
  };

  // Mapear categoria para faixa de inadimplência
  const getFaixaInadimplencia = (category: string | null) => {
    switch (category) {
      case '1-2':
        return '1-2 dias';
      case '3-6':
        return '3-6 dias';
      case '7+':
        return '7+ dias';
      default:
        return '';
    }
  };

  const { data: clientesData, isLoading, error } = useQuery({
    queryKey: ['aging-clientes', category, selectedCompany],
    queryFn: async () => {
      if (!category) return { clientes: [] };
      
      const params = new URLSearchParams();
      params.append('faixa', getFaixaInadimplencia(category));
      if (selectedCompany) {
        params.append('companyId', selectedCompany);
      }
      
      const response = await api.get(`/reports/aging/clientes?${params.toString()}`);
      return response.data.data;
    },
    enabled: isOpen && !!category,
    retry: 1,
  });

  // Buscar etapas do Kanban para usar a primeira como padrão
  const { data: kanbanData } = useQuery({
    queryKey: ['kanban-etapas-ativas'],
    queryFn: () => api.get('/kanban-etapas/ativas').then(res => res.data),
    enabled: isOpen
  });

  const clientes = clientesData?.clientes || [];

  // Agrupar clientes por empresa baseado nos títulos
  const clientesPorEmpresa = clientes.reduce((acc: Record<string, { clientes: ClienteData[], empresa: { id: string, name: string } }>, cliente: ClienteData) => {
    // Pegar a empresa do primeiro título do cliente
    const primeiroTitulo = cliente.titulos[0];
    if (!primeiroTitulo) return acc;
    
    const empresaId = primeiroTitulo.companyId;
    const empresaName = primeiroTitulo.company?.name || primeiroTitulo.empresa || cliente.company?.name || cliente.empresa || 'Empresa não identificada';
    
    // Adicionar informações do operador e kanban do primeiro título
    if (primeiroTitulo.operador) {
      cliente.operador = primeiroTitulo.operador;
    }
    
    // Se não tem etapa definida, usar a primeira etapa do Kanban
    if (primeiroTitulo.kanbanEtapa) {
      cliente.kanbanEtapa = primeiroTitulo.kanbanEtapa;
    } else if (kanbanData?.data && kanbanData.data.length > 0) {
      // Usar a primeira etapa do Kanban como padrão
      const primeiraEtapa = kanbanData.data[0];
      cliente.kanbanEtapa = {
        id: primeiraEtapa.id,
        nome: primeiraEtapa.nome,
        cor: primeiraEtapa.cor
      };
    }
    
    if (!acc[empresaId]) {
      acc[empresaId] = {
        clientes: [],
        empresa: { id: empresaId, name: empresaName }
      };
    }
    acc[empresaId].clientes.push(cliente);
    return acc;
  }, {});

  // Calcular totais por empresa
  const getTotaisPorEmpresa = (clientes: ClienteData[]) => {
    return clientes.reduce((acc, cliente) => {
      acc.totalTitulos += cliente.quantidadeTitulos;
      acc.valorTotal += cliente.valorTotal;
      acc.totalClientes += 1;
      return acc;
    }, { totalTitulos: 0, valorTotal: 0, totalClientes: 0 });
  };

  const getCategoryTitle = (category: string | null) => {
    switch (category) {
      case '1-2':
        return '1 a 2 Dias Vencidos';
      case '3-6':
        return '3 a 6 Dias Vencidos';
      case '7+':
        return 'Acima de 7 Dias Vencidos';
      default:
        return 'Clientes';
    }
  };

  const getCategoryColor = (category: string | null) => {
    switch (category) {
      case '1-2':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case '3-6':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case '7+':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Função para exportar para Excel (CSV)
  const handleExportExcel = (clientes: ClienteData[], empresaNome: string) => {
    try {
      // Criar cabeçalhos CSV
      const headers = [
        'Cliente',
        'Documento',
        'Email',
        'Quantidade de Títulos',
        'Valor Total',
        'Títulos Vencidos',
        'Títulos Parciais',
        'Títulos Abertos',
        'Valor Vencido',
        'Valor Parcial',
        'Valor Aberto',
        'Operador',
        'Etapa Kanban',
        'Empresa'
      ];

      // Criar linhas de dados
      const rows = clientes.map(cliente => [
        cliente.nome || '',
        cliente.documento || '',
        cliente.email || '',
        cliente.quantidadeTitulos || 0,
        cliente.valorTotal || 0,
        cliente.titulosVencidos || 0,
        cliente.titulosParciais || 0,
        cliente.titulosAbertos || 0,
        cliente.valorVencido || 0,
        cliente.valorParcial || 0,
        cliente.valorAberto || 0,
        cliente.operador || 'Sem operador',
        cliente.kanbanEtapa?.nome || 'Sem etapa',
        empresaNome || cliente.empresa || 'Empresa não identificada'
      ]);

      // Combinar cabeçalhos e linhas
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => {
          // Escapar vírgulas e aspas
          const cellStr = String(cell);
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(','))
      ].join('\n');

      // Criar blob e fazer download
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const fileName = `inadimplencia_clientes_${empresaNome.replace(/[^a-z0-9]/gi, '_')}_${getCategoryTitle(category).replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao exportar para Excel:', error);
      alert('Erro ao exportar para Excel');
    }
  };

  // Função para exportar para PDF
  const handleExportPDF = (clientes: ClienteData[], empresaNome: string) => {
    try {
      // Criar conteúdo HTML para o PDF
      const htmlContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <title>Relatório de Inadimplência - ${empresaNome}</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                padding: 20px;
                color: #333;
              }
              h1 {
                color: #1f2937;
                border-bottom: 2px solid #3b82f6;
                padding-bottom: 10px;
                margin-bottom: 20px;
              }
              h2 {
                color: #4b5563;
                margin-top: 30px;
                margin-bottom: 15px;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 30px;
              }
              th, td {
                border: 1px solid #ddd;
                padding: 12px;
                text-align: left;
              }
              th {
                background-color: #3b82f6;
                color: white;
                font-weight: bold;
              }
              tr:nth-child(even) {
                background-color: #f9fafb;
              }
              .total-row {
                background-color: #e5e7eb;
                font-weight: bold;
              }
              .header-info {
                margin-bottom: 20px;
                padding: 15px;
                background-color: #f3f4f6;
                border-radius: 5px;
              }
              .header-info p {
                margin: 5px 0;
              }
            </style>
          </head>
          <body>
            <h1>Relatório de Inadimplência - ${empresaNome}</h1>
            <div class="header-info">
              <p><strong>Categoria:</strong> ${getCategoryTitle(category)}</p>
              <p><strong>Data de Geração:</strong> ${new Date().toLocaleDateString('pt-BR')} ${new Date().toLocaleTimeString('pt-BR')}</p>
              <p><strong>Total de Clientes:</strong> ${clientes.length}</p>
              <p><strong>Total de Títulos:</strong> ${clientes.reduce((sum, c) => sum + (c.quantidadeTitulos || 0), 0)}</p>
              <p><strong>Valor Total:</strong> ${formatCurrency(clientes.reduce((sum, c) => sum + (c.valorTotal || 0), 0))}</p>
            </div>
            <h2>Detalhamento por Cliente</h2>
            <table>
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Documento</th>
                  <th>Email</th>
                  <th>Qtd. Títulos</th>
                  <th>Valor Total</th>
                  <th>Títulos Vencidos</th>
                  <th>Títulos Parciais</th>
                  <th>Valor Vencido</th>
                  <th>Operador</th>
                  <th>Etapa Kanban</th>
                </tr>
              </thead>
              <tbody>
                ${clientes.map(cliente => `
                  <tr>
                    <td>${cliente.nome || ''}</td>
                    <td>${cliente.documento || ''}</td>
                    <td>${cliente.email || ''}</td>
                    <td>${cliente.quantidadeTitulos || 0}</td>
                    <td>${formatCurrency(cliente.valorTotal || 0)}</td>
                    <td>${cliente.titulosVencidos || 0}</td>
                    <td>${cliente.titulosParciais || 0}</td>
                    <td>${formatCurrency(cliente.valorVencido || 0)}</td>
                    <td>${cliente.operador || 'Sem operador'}</td>
                    <td>${cliente.kanbanEtapa?.nome || 'Sem etapa'}</td>
                  </tr>
                `).join('')}
                <tr class="total-row">
                  <td colspan="3"><strong>TOTAL</strong></td>
                  <td><strong>${clientes.reduce((sum, c) => sum + (c.quantidadeTitulos || 0), 0)}</strong></td>
                  <td><strong>${formatCurrency(clientes.reduce((sum, c) => sum + (c.valorTotal || 0), 0))}</strong></td>
                  <td><strong>${clientes.reduce((sum, c) => sum + (c.titulosVencidos || 0), 0)}</strong></td>
                  <td><strong>${clientes.reduce((sum, c) => sum + (c.titulosParciais || 0), 0)}</strong></td>
                  <td><strong>${formatCurrency(clientes.reduce((sum, c) => sum + (c.valorVencido || 0), 0))}</strong></td>
                  <td colspan="2"></td>
                </tr>
              </tbody>
            </table>
          </body>
        </html>
      `;

      // Abrir nova janela e imprimir (que permite salvar como PDF)
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (error) {
      console.error('Erro ao exportar para PDF:', error);
      alert('Erro ao exportar para PDF');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={getCategoryTitle(category)}
      size="xl"
    >
      <div className="space-y-6">
        {/* Header com resumo geral */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`px-3 py-1 rounded-full border text-sm font-medium ${getCategoryColor(category)}`}>
              <Calendar className="w-4 h-4 inline mr-1" />
              {getCategoryTitle(category)}
            </div>
          </div>
          <div className="text-sm text-muted-foreground">
            {clientes.length} cliente{clientes.length !== 1 ? 's' : ''} encontrado{clientes.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            <span>Carregando clientes...</span>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="text-center py-8 text-red-600">
            Erro ao carregar dados dos clientes
          </div>
        )}

        {/* Conteúdo principal */}
        {!isLoading && !error && (
          <div className="space-y-4">
            {Object.keys(clientesPorEmpresa).length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum cliente encontrado nesta categoria
              </div>
            ) : (
              Object.entries(clientesPorEmpresa).map(([empresaId, empresaData]: [string, any]) => {
                const totais = getTotaisPorEmpresa(empresaData.clientes);
                const empresa = empresaData.empresa;

                return (
                  <Card key={empresaId}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Building2 className="w-5 h-5 text-blue-600" />
                          <CardTitle className="text-lg">{empresa.name}</CardTitle>
                        </div>
                        <div className="flex items-center space-x-6">
                          <div className="flex items-center space-x-4 text-lg font-semibold">
                            <div className="flex items-center space-x-2">
                              <Users className="w-5 h-5 text-blue-600" />
                              <span className="text-gray-900 dark:text-slate-100">{totais.totalClientes} cliente{totais.totalClientes !== 1 ? 's' : ''}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <FileText className="w-5 h-5 text-blue-600" />
                              <span className="text-gray-900 dark:text-slate-100">{totais.totalTitulos} título{totais.totalTitulos !== 1 ? 's' : ''}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <DollarSign className="w-5 h-5 text-green-600" />
                              <span className="text-gray-900 dark:text-slate-100">{formatCurrency(totais.valorTotal)}</span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExportExcel(empresaData.clientes, empresa.name);
                              }}
                              className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors"
                              title="Exportar para Excel"
                            >
                              <FileSpreadsheet className="w-4 h-4" />
                              <span>Excel</span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExportPDF(empresaData.clientes, empresa.name);
                              }}
                              className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
                              title="Exportar para PDF"
                            >
                              <FileTextIcon className="w-4 h-4" />
                              <span>PDF</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {empresaData.clientes.map((cliente: any) => (
                          <div
                            key={cliente.id}
                            className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-800 rounded-lg border cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                            onClick={() => handleClienteClick(cliente)}
                          >
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <div className="font-medium text-gray-900 dark:text-slate-100">
                                  {cliente.nome}
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenClienteTimeline(cliente);
                                  }}
                                  className="flex items-center space-x-1 text-xs text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                                  title="Abrir timeline do cliente em nova aba"
                                >
                                  <Eye className="w-3 h-3" />
                                  <span>Ver histórico</span>
                                </button>
                              </div>
                              <div className="text-sm text-muted-foreground mb-1">
                                {cliente.quantidadeTitulos} título{cliente.quantidadeTitulos !== 1 ? 's' : ''} • 
                                {cliente.documento && `Doc: ${cliente.documento}`}
                              </div>
                              
                              {/* Informações do operador e status do funil */}
                              <div className="flex items-center space-x-3 text-xs">
                                {cliente.operador && (
                                  <div className="flex items-center space-x-1 text-blue-600">
                                    <User className="w-3 h-3" />
                                    <span>{cliente.operador.name}</span>
                                  </div>
                                )}
                                {cliente.kanbanEtapa && (
                                  <div className="flex items-center space-x-1">
                                    <div 
                                      className="w-2 h-2 rounded-full" 
                                      style={{ backgroundColor: cliente.kanbanEtapa.cor || '#6b7280' }}
                                    />
                                    <span className="text-muted-foreground">{cliente.kanbanEtapa.nome}</span>
                                  </div>
                                )}
                              </div>
                              
                              {cliente.email && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  {cliente.email}
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="font-semibold text-gray-900 dark:text-slate-100">
                                {formatCurrency(cliente.valorTotal)}
                              </div>
                              <div className="flex space-x-1 mt-1">
                                {cliente.titulosVencidos > 0 && (
                                  <Badge variant="destructive" className="text-xs">
                                    {cliente.titulosVencidos} vencido{cliente.titulosVencidos !== 1 ? 's' : ''}
                                  </Badge>
                                )}
                                {cliente.titulosParciais > 0 && (
                                  <Badge variant="secondary" className="text-xs">
                                    {cliente.titulosParciais} parcial{cliente.titulosParciais !== 1 ? 's' : ''}
                                  </Badge>
                                )}
                                {cliente.titulosAbertos > 0 && (
                                  <Badge variant="outline" className="text-xs">
                                    {cliente.titulosAbertos} aberto{cliente.titulosAbertos !== 1 ? 's' : ''}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Modal do Cliente com Histórico */}
      {selectedCliente && isClienteModalOpen && (
        <ClienteFornecedorModal
          cliente={selectedCliente as any}
          isOpen={isClienteModalOpen}
          onClose={handleCloseClienteModal}
        />
      )}
    </Modal>
  );
};
